import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  ChevronDown,
  Flag,
  MessageCircle,
  Minus,
  Send,
  Smile,
  Trash2,
  Users,
} from "lucide-react";
import { apiFetch } from "@/lib/api";
import { cn } from "@/lib/cn";
import { useErrorToast, useToast } from "@/components/ui/toast";
import { Button, Spinner } from "@/components/ui/primitives";
import { Dialog } from "@/components/ui/dialog";
import { CHAT_EMOJI_GROUPS } from "./chat-emoji";
import type { ChatMessageDto, ChatPageDto, ChatRoomDto } from "@/lib/types";

const ROOM_POLL_MS = 30_000;
const MESSAGE_POLL_MS = 8_000;
const PAGE_SIZE = 50;
const MAX_BODY = 2000;

interface ChatDockValue {
  rooms: ChatRoomDto[];
  totalUnread: number;
  /** Unread waiting in one specific room, for a badge somewhere else in the app. */
  unreadFor: (challengeId: string, teamId: string | null) => number;
  hasRoom: (challengeId: string, teamId: string | null) => boolean;
  openRoom: (challengeId: string, teamId: string | null) => void;
  refreshRooms: () => void;
}

const ChatDockContext = createContext<ChatDockValue | null>(null);

/**
 * Returns the dock, or null where it is not mounted.
 *
 * Nullable on purpose: the dock only exists for a signed-in reader inside the app shell, and a
 * page that merely wants to offer a way in should render nothing rather than crash outside it.
 */
export function useChatDock(): ChatDockValue | null {
  return useContext(ChatDockContext);
}

function roomKey(challengeId: string, teamId: string | null): string {
  return `${challengeId}:${teamId ?? "campaign"}`;
}

function messagesPath(
  challengeId: string,
  teamId: string | null,
  before?: string,
): string {
  const params = new URLSearchParams({ challengeId, take: String(PAGE_SIZE) });
  if (teamId) params.set("teamId", teamId);
  if (before) params.set("before", before);
  return `/api/chat/messages?${params.toString()}`;
}

/** Short and local: inside a conversation the date matters far less than the time of day. */
function formatStamp(value: string): string {
  const date = new Date(value);
  const today = new Date();
  const sameDay = date.toDateString() === today.toDateString();
  return sameDay
    ? date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
    : date.toLocaleString(undefined, {
        day: "numeric",
        month: "short",
        hour: "numeric",
        minute: "2-digit",
      });
}

/**
 * Holds the reader's rooms and the launcher, and lets any page ask for a room to be opened.
 *
 * The rooms list is polled rather than pushed: there is no realtime transport in the portal, and a
 * badge that is a few seconds stale is a fair trade for not introducing one.
 */
export function ChatDockProvider({ children }: { children: ReactNode }) {
  const [rooms, setRooms] = useState<ChatRoomDto[]>([]);
  const [open, setOpen] = useState(false);
  const [activeKey, setActiveKey] = useState<string | null>(null);

  const refreshRooms = useCallback(() => {
    void apiFetch<ChatRoomDto[]>("/api/chat/rooms")
      .then(setRooms)
      // A chat launcher is not worth interrupting anybody for. If the poll fails the badge simply
      // does not move, and the next tick tries again.
      .catch(() => undefined);
  }, []);

  // Polling is paused for a hidden tab: the launcher badge is invisible then, and this provider is
  // mounted for every signed-in reader, so a background tab left open all day would otherwise keep
  // asking the server for rooms nobody is looking at. Returning to the tab refreshes immediately.
  useEffect(() => {
    refreshRooms();
    const tick = () => {
      if (document.visibilityState === "visible") refreshRooms();
    };
    const timer = window.setInterval(tick, ROOM_POLL_MS);
    document.addEventListener("visibilitychange", tick);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [refreshRooms]);

  const active = useMemo(
    () => rooms.find((room) => roomKey(room.challengeId, room.teamId) === activeKey) ?? null,
    [rooms, activeKey],
  );

  const value = useMemo<ChatDockValue>(
    () => ({
      rooms,
      totalUnread: rooms.reduce((sum, room) => sum + room.unreadCount, 0),
      unreadFor: (challengeId, teamId) =>
        rooms.find((room) => roomKey(room.challengeId, room.teamId) === roomKey(challengeId, teamId))
          ?.unreadCount ?? 0,
      hasRoom: (challengeId, teamId) =>
        rooms.some(
          (room) => roomKey(room.challengeId, room.teamId) === roomKey(challengeId, teamId),
        ),
      openRoom: (challengeId, teamId) => {
        setActiveKey(roomKey(challengeId, teamId));
        setOpen(true);
      },
      refreshRooms,
    }),
    [rooms, refreshRooms],
  );

  return (
    <ChatDockContext.Provider value={value}>
      {children}
      {rooms.length > 0 && (
        <ChatDock
          rooms={rooms}
          open={open}
          active={active}
          onOpen={() => {
            setOpen(true);
            if (!activeKey) {
              setActiveKey(roomKey(rooms[0].challengeId, rooms[0].teamId));
            }
          }}
          onMinimize={() => setOpen(false)}
          onSelect={(room) => setActiveKey(roomKey(room.challengeId, room.teamId))}
          onChanged={refreshRooms}
        />
      )}
    </ChatDockContext.Provider>
  );
}

function ChatDock({
  rooms,
  open,
  active,
  onOpen,
  onMinimize,
  onSelect,
  onChanged,
}: {
  rooms: ChatRoomDto[];
  open: boolean;
  active: ChatRoomDto | null;
  onOpen: () => void;
  onMinimize: () => void;
  onSelect: (room: ChatRoomDto) => void;
  onChanged: () => void;
}) {
  const totalUnread = rooms.reduce((sum, room) => sum + room.unreadCount, 0);

  // Clear of the mobile bottom nav, which owns the bottom of the viewport on small screens.
  const anchor =
    "fixed right-4 z-40 bottom-[calc(5rem+env(safe-area-inset-bottom))] lg:bottom-6";

  if (!open) {
    return (
      <button
        type="button"
        onClick={onOpen}
        title="Team and campaign chat"
        aria-label={
          totalUnread > 0 ? `Open chat, ${totalUnread} unread` : "Open chat"
        }
        className={cn(
          anchor,
          // Labelled where there is room for a label: a bare bubble is a convention people have to
          // already know, and this is the first chat the portal has ever had.
          "flex h-13 items-center justify-center gap-2 rounded-full bg-[var(--brand-accent)] px-4 text-white",
          "text-sm font-bold tracking-tight",
          // The launcher floats over whatever happens to be beneath it, including the navy footer,
          // where a brand-primary button vanishes. Accent plus a light ring separates it from both.
          "ring-2 ring-white/70",
          "shadow-[var(--shadow-accent)] transition-transform duration-200 ease-out hover:scale-105 active:scale-95",
        )}
      >
        <MessageCircle className="h-5 w-5" />
        <span className="hidden sm:inline">Chat</span>
        {totalUnread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-white px-1 text-[11px] font-extrabold text-[var(--brand-accent)] ring-1 ring-[var(--brand-accent)]">
            {totalUnread > 99 ? "99+" : totalUnread}
          </span>
        )}
      </button>
    );
  }

  return (
    <div
      className={cn(
        anchor,
        "left-4 flex max-h-[min(32rem,calc(100dvh-8rem))] w-auto flex-col overflow-hidden",
        "rounded-2xl border border-border bg-surface shadow-[var(--shadow-lg)] sm:left-auto sm:w-[22rem]",
      )}
    >
      <ChatHeader
        rooms={rooms}
        active={active}
        onSelect={onSelect}
        onMinimize={onMinimize}
      />
      {active ? (
        <ChatRoomPanel room={active} onChanged={onChanged} />
      ) : (
        <p className="p-6 text-center text-sm text-text-muted">
          Pick a conversation to get started.
        </p>
      )}
    </div>
  );
}

function ChatHeader({
  rooms,
  active,
  onSelect,
  onMinimize,
}: {
  rooms: ChatRoomDto[];
  active: ChatRoomDto | null;
  onSelect: (room: ChatRoomDto) => void;
  onMinimize: () => void;
}) {
  const [switching, setSwitching] = useState(false);

  return (
    <div className="border-b border-border">
      <div className="flex items-center gap-2 px-3 py-2.5">
        <span
          aria-hidden
          className="h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ background: active?.accentColor ?? "var(--brand-primary)" }}
        />
        <button
          type="button"
          onClick={() => setSwitching((value) => !value)}
          disabled={rooms.length < 2}
          className="flex min-w-0 flex-1 items-center gap-1 text-left disabled:cursor-default"
        >
          <span className="truncate text-sm font-semibold text-text">
            {active?.name ?? "Chat"}
          </span>
          {rooms.length > 1 && (
            <ChevronDown
              className={cn(
                "h-4 w-4 shrink-0 text-text-muted transition-transform",
                switching && "rotate-180",
              )}
            />
          )}
        </button>
        <button
          type="button"
          onClick={onMinimize}
          aria-label="Minimize chat"
          className="rounded-lg p-1.5 text-text-muted hover:bg-surface-muted hover:text-text"
        >
          <Minus className="h-4 w-4" />
        </button>
      </div>

      {switching && rooms.length > 1 && (
        <ul className="max-h-48 overflow-y-auto border-t border-border bg-surface-muted/40 py-1">
          {rooms.map((room) => {
            const key = roomKey(room.challengeId, room.teamId);
            const isActive =
              active !== null && key === roomKey(active.challengeId, active.teamId);
            return (
              <li key={key}>
                <button
                  type="button"
                  onClick={() => {
                    onSelect(room);
                    setSwitching(false);
                  }}
                  className={cn(
                    "flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-surface-muted",
                    isActive && "font-semibold text-text",
                  )}
                >
                  {room.scope === "Team" ? (
                    <Users className="h-3.5 w-3.5 shrink-0 text-text-muted" />
                  ) : (
                    <MessageCircle className="h-3.5 w-3.5 shrink-0 text-text-muted" />
                  )}
                  <span className="min-w-0 flex-1 truncate">{room.name}</span>
                  {room.unreadCount > 0 && (
                    <span className="rounded-full bg-[var(--danger)] px-1.5 text-[11px] font-bold text-white">
                      {room.unreadCount}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function ChatRoomPanel({
  room,
  onChanged,
}: {
  room: ChatRoomDto;
  onChanged: () => void;
}) {
  const [page, setPage] = useState<ChatPageDto | null>(null);
  const [older, setOlder] = useState<ChatMessageDto[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [reporting, setReporting] = useState<ChatMessageDto | null>(null);

  const errorToast = useErrorToast();
  const { success } = useToast();
  const scrollRef = useRef<HTMLDivElement>(null);
  const key = roomKey(room.challengeId, room.teamId);

  const messages = useMemo(
    () => [...older, ...(page?.messages ?? [])],
    [older, page],
  );

  const load = useCallback(
    async (announceErrors: boolean) => {
      try {
        const result = await apiFetch<ChatPageDto>(
          messagesPath(room.challengeId, room.teamId),
        );
        setPage(result);
        setHasMore(result.hasMore);
        setLoadError(null);
      } catch (e) {
        // A failed poll is silent; a failed first load has to say so, or the panel is just empty.
        if (announceErrors) {
          setLoadError(
            e instanceof Error ? e.message : "This conversation could not be loaded.",
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [room.challengeId, room.teamId],
  );

  // Switching rooms is a fresh conversation, not an update to the current one.
  useEffect(() => {
    setPage(null);
    setOlder([]);
    setHasMore(false);
    setLoading(true);
    setLoadError(null);
    setShowEmoji(false);
    void load(true);
  }, [key, load]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void load(false);
    }, MESSAGE_POLL_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  // Having the room on screen is what "read" means, so the marker follows what arrives in it.
  const newest = messages.at(-1)?.createdAtUtc ?? null;
  useEffect(() => {
    if (!newest || !room.canPost) return;
    void apiFetch("/api/chat/read", {
      method: "POST",
      body: { challengeId: room.challengeId, teamId: room.teamId },
    })
      .then(onChanged)
      .catch(() => undefined);
  }, [newest, room.canPost, room.challengeId, room.teamId, onChanged]);

  useEffect(() => {
    const element = scrollRef.current;
    if (element) element.scrollTop = element.scrollHeight;
  }, [newest, loading]);

  async function loadOlder() {
    const oldest = messages[0];
    if (!oldest) return;
    try {
      const result = await apiFetch<ChatPageDto>(
        messagesPath(room.challengeId, room.teamId, oldest.createdAtUtc),
      );
      setOlder((current) => [...result.messages, ...current]);
      setHasMore(result.hasMore);
    } catch (e) {
      errorToast(e, "Earlier messages could not be loaded.");
    }
  }

  async function send() {
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      await apiFetch<ChatMessageDto>("/api/chat/messages", {
        method: "POST",
        body: { challengeId: room.challengeId, teamId: room.teamId, body },
      });
      setDraft("");
      setShowEmoji(false);
      await load(true);
      onChanged();
    } catch (e) {
      errorToast(e, "Your message was not sent.");
    } finally {
      setSending(false);
    }
  }

  async function withdraw(message: ChatMessageDto) {
    try {
      await apiFetch(`/api/chat/messages/${message.id}`, { method: "DELETE" });
      await load(true);
    } catch (e) {
      errorToast(e, "That message could not be taken back.");
    }
  }

  async function report(message: ChatMessageDto, reason: string) {
    try {
      await apiFetch(`/api/chat/messages/${message.id}/report`, {
        method: "POST",
        body: { reason },
      });
      setReporting(null);
      await load(true);
      success("Reported", "An organiser will take a look.");
    } catch (e) {
      errorToast(e, "That report could not be sent.");
    }
  }

  return (
    <>
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3">
        {loading ? (
          <div className="flex justify-center py-8">
            <Spinner className="h-5 w-5" />
          </div>
        ) : loadError ? (
          <p className="py-8 text-center text-sm text-text-muted">{loadError}</p>
        ) : messages.length === 0 ? (
          <p className="py-8 text-center text-sm text-text-muted">
            Nothing here yet. Say hello.
          </p>
        ) : (
          <>
            {hasMore && (
              <div className="mb-2 flex justify-center">
                <Button variant="ghost" size="sm" onClick={loadOlder}>
                  Load earlier messages
                </Button>
              </div>
            )}
            <ul className="space-y-2.5">
              {messages.map((message) => (
                <ChatBubble
                  key={message.id}
                  message={message}
                  canModerate={room.canPost}
                  onWithdraw={() => withdraw(message)}
                  onReport={() => setReporting(message)}
                />
              ))}
            </ul>
          </>
        )}
      </div>

      {room.canPost ? (
        <div className="border-t border-border p-2">
          {showEmoji && (
            <div className="mb-2 max-h-40 overflow-y-auto rounded-xl border border-border bg-surface-muted/40 p-2">
              {CHAT_EMOJI_GROUPS.map((group) => (
                <div key={group.label} className="mb-1.5 last:mb-0">
                  <p className="px-0.5 pb-1 text-[11px] font-semibold uppercase tracking-wide text-text-subtle">
                    {group.label}
                  </p>
                  <div className="grid grid-cols-8 gap-0.5">
                    {group.emoji.map((emoji) => (
                      <button
                        key={emoji}
                        type="button"
                        aria-label={`Insert ${emoji}`}
                        onClick={() =>
                          setDraft((value) => (value + emoji).slice(0, MAX_BODY))
                        }
                        className="rounded-lg py-1 text-lg hover:bg-surface-muted"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-end gap-1.5">
            <button
              type="button"
              onClick={() => setShowEmoji((value) => !value)}
              aria-label="Emoji"
              aria-pressed={showEmoji}
              className="rounded-lg p-2 text-text-muted hover:bg-surface-muted hover:text-text"
            >
              <Smile className="h-4.5 w-4.5" />
            </button>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value.slice(0, MAX_BODY))}
              onKeyDown={(e) => {
                // Enter sends; Shift+Enter is a new line, as in every other chat.
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void send();
                }
              }}
              rows={1}
              placeholder={`Message ${room.name}`}
              aria-label={`Message ${room.name}`}
              className="max-h-24 min-h-9 flex-1 resize-none rounded-xl border border-border bg-surface px-3 py-2 text-sm text-text outline-none focus:border-[var(--brand-primary)]"
            />
            <Button
              size="sm"
              onClick={() => void send()}
              disabled={draft.trim().length === 0}
              loading={sending}
              aria-label="Send"
              className="h-9 w-9 justify-center px-0"
            >
              {!sending && <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      ) : (
        <p className="border-t border-border px-3 py-2.5 text-xs text-text-muted">
          {room.readOnlyReason ?? "This conversation is read-only."}
        </p>
      )}

      <ReportDialog
        message={reporting}
        onClose={() => setReporting(null)}
        onSubmit={report}
      />
    </>
  );
}

function ChatBubble({
  message,
  canModerate,
  onWithdraw,
  onReport,
}: {
  message: ChatMessageDto;
  canModerate: boolean;
  onWithdraw: () => void;
  onReport: () => void;
}) {
  if (message.isWithdrawn) {
    return (
      <li className={cn("flex", message.isMine ? "justify-end" : "justify-start")}>
        <p className="rounded-xl bg-surface-muted/60 px-3 py-1.5 text-xs italic text-text-subtle">
          {message.isMine ? "You took this back" : `${message.authorName} took this back`}
        </p>
      </li>
    );
  }

  return (
    <li className={cn("group flex", message.isMine ? "justify-end" : "justify-start")}>
      <div className={cn("max-w-[85%]", message.isMine && "text-right")}>
        {!message.isMine && (
          <p className="px-1 pb-0.5 text-[11px] font-semibold text-text-muted">
            {message.authorName}
          </p>
        )}
        <div
          className={cn(
            "rounded-2xl px-3 py-2 text-sm",
            message.isMine
              ? "bg-[var(--brand-primary)] text-white"
              : "bg-surface-muted text-text",
          )}
        >
          <p className="whitespace-pre-wrap break-words text-left">{message.body}</p>
        </div>
        <div className="flex items-center gap-2 px-1 pt-0.5">
          <span className="text-[11px] text-text-subtle">
            {formatStamp(message.createdAtUtc)}
          </span>
          {canModerate && message.isMine && (
            <button
              type="button"
              onClick={onWithdraw}
              className="text-text-subtle opacity-0 transition-opacity hover:text-[var(--danger)] focus:opacity-100 group-hover:opacity-100"
              aria-label="Take this message back"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
          {canModerate && !message.isMine && !message.isReportedByMe && (
            <button
              type="button"
              onClick={onReport}
              className="text-text-subtle opacity-0 transition-opacity hover:text-[var(--danger)] focus:opacity-100 group-hover:opacity-100"
              aria-label="Report this message"
            >
              <Flag className="h-3.5 w-3.5" />
            </button>
          )}
          {message.isReportedByMe && (
            <span className="text-[11px] text-text-subtle">Reported</span>
          )}
        </div>
      </div>
    </li>
  );
}

function ReportDialog({
  message,
  onClose,
  onSubmit,
}: {
  message: ChatMessageDto | null;
  onClose: () => void;
  onSubmit: (message: ChatMessageDto, reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (message) setReason("");
  }, [message]);

  if (!message) return null;

  return (
    <Dialog
      open
      onClose={onClose}
      title="Report this message"
      description="Organisers can read the reported message and the room it was in until they close the report. They cannot delete anybody else's messages."
    >
      <blockquote className="mb-3 rounded-xl border border-border bg-surface-muted/50 p-3 text-sm text-text-muted">
        <span className="font-semibold text-text">{message.authorName}: </span>
        {message.body}
      </blockquote>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value.slice(0, 500))}
        rows={3}
        placeholder="What is wrong with it?"
        aria-label="Reason for reporting"
        className="w-full resize-none rounded-xl border border-border bg-surface px-3 py-2 text-sm text-text outline-none focus:border-[var(--brand-primary)]"
      />
      <div className="mt-3 flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="danger"
          size="sm"
          loading={busy}
          disabled={reason.trim().length === 0}
          onClick={async () => {
            setBusy(true);
            try {
              await onSubmit(message, reason.trim());
            } finally {
              setBusy(false);
            }
          }}
        >
          <Flag className="h-4 w-4" />
          Report
        </Button>
      </div>
    </Dialog>
  );
}

